import { t, Instance, flow } from 'mobx-state-tree';
import { FactTypeModel, createFactTypeModelFromResponse } from './models/FactType';
import { factTypesApi } from '@/api';
import type { CreateFactTypeRequest, UpdateFactTypeRequest, FactTypeResponse } from 'we-tree-api-client/v2';

/**
 * FactTypesStore - Manages fact types from the database
 * Handles loading, caching, and CRUD operations for fact types
 */
export const FactTypesStore = t
  .model('FactTypesStore', {
    factTypes: t.map(FactTypeModel),
    isLoading: t.optional(t.boolean, false),
    lastLoadedAt: t.maybeNull(t.Date),
    error: t.maybeNull(t.string)
  })
  .views((self) => ({
    /**
     * Get all fact types as an array
     */
    get allFactTypes(): Instance<typeof FactTypeModel>[] {
      return Array.from(self.factTypes.values());
    },

    /**
     * Get fact types available for selection in the dropdown
     * Shows all fact types if none are marked as active/system (for backward compatibility)
     * Otherwise shows active or system fact types
     */
    get activeFactTypes(): Instance<typeof FactTypeModel>[] {
      const allTypes = Array.from(self.factTypes.values());
      const activeOrSystem = allTypes.filter((ft) => ft.isActive || ft.isSystem);
      // If no fact types are marked as active or system, show all (they're likely all system types)
      // This handles the case where the database hasn't been updated with proper flags yet
      return activeOrSystem.length > 0 ? activeOrSystem : allTypes;
    },

    /**
     * Get a fact type by ID
     */
    getFactTypeById(factTypeId: string): Instance<typeof FactTypeModel> | undefined {
      return self.factTypes.get(factTypeId);
    },

    /**
     * Get a fact type by slug
     */
    getFactTypeBySlug(slug: string): Instance<typeof FactTypeModel> | undefined {
      return Array.from(self.factTypes.values()).find((ft) => ft.slug === slug);
    }
  }))
  .actions((self) => ({
    /**
     * Clear all fact types
     */
    clear() {
      self.factTypes.clear();
      self.lastLoadedAt = null;
      self.error = null;
    },

    /**
     * Set error message
     */
    setError(errorMessage: string | null) {
      self.error = errorMessage;
    },

    /**
     * Load fact types from the API
     * @param isActive - If true, only load active fact types. If false or undefined, load all.
     */
    loadFactTypes: flow(function* (isActive?: boolean) {
      self.isLoading = true;
      self.error = null;

      try {
        const result = yield factTypesApi.listFactTypes(isActive);

        if (result.success) {
          const factTypes = result.data.factTypes || [];

          // Clear existing fact types and add new ones
          self.factTypes.clear();
          factTypes.forEach((factType: FactTypeResponse) => {
            try {
              const factTypeModel = createFactTypeModelFromResponse(factType);
              self.factTypes.set(factType.id, factTypeModel);
            } catch (error) {
              // Log the problematic fact type for debugging
              // eslint-disable-next-line no-console
              console.error('[FactTypesStore] Failed to create fact type model:', {
                factType,
                error: error instanceof Error ? error.message : String(error)
              });
              // Skip this fact type and continue with others
            }
          });

          self.lastLoadedAt = new Date();
          self.error = null;
        } else {
          const errorMessage = result.error?.getUserMessage() || 'Failed to load fact types';
          self.error = errorMessage;
          // eslint-disable-next-line no-console
          console.error('[FactTypesStore] Failed to load fact types:', errorMessage, result.error);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        self.error = errorMessage;
        // eslint-disable-next-line no-console
        console.error('[FactTypesStore] Error loading fact types:', error);
      } finally {
        self.isLoading = false;
      }
    }),

    /**
     * Get a fact type by ID, loading from API if not in store
     */
    getFactType: flow(function* (factTypeId: string) {
      // Check if already in store
      const existing = self.factTypes.get(factTypeId);
      if (existing) {
        return { success: true, data: existing };
      }

      // Load from API
      try {
        const result = yield factTypesApi.getFactType(factTypeId);
        if (result.success && result.data) {
          const factTypeModel = createFactTypeModelFromResponse(result.data);
          self.factTypes.set(factTypeId, factTypeModel);
          return { success: true, data: factTypeModel };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to get fact type')
        };
      }
    }),

    /**
     * Get a fact type by slug, loading from API if not in store
     */
    getFactTypeBySlugAction: flow(function* (slug: string) {
      // Check if already in store
      const existing = Array.from(self.factTypes.values()).find((ft) => ft.slug === slug);
      if (existing) {
        return { success: true, data: existing };
      }

      // Load from API
      try {
        const result = yield factTypesApi.getFactTypeBySlug(slug);
        if (result.success && result.data) {
          const factTypeModel = createFactTypeModelFromResponse(result.data);
          self.factTypes.set(result.data.id, factTypeModel);
          return { success: true, data: factTypeModel };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to get fact type by slug')
        };
      }
    }),

    /**
     * Create a new fact type
     * Note: This action should only be available to users with appropriate permissions
     */
    createFactType: flow(function* (request: CreateFactTypeRequest) {
      try {
        const result = yield factTypesApi.createFactType(request);
        if (result.success && result.data) {
          const factTypeModel = createFactTypeModelFromResponse(result.data);
          self.factTypes.set(result.data.id, factTypeModel);
          return { success: true, data: factTypeModel };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to create fact type')
        };
      }
    }),

    /**
     * Update an existing fact type
     * Note: This action should only be available to users with appropriate permissions
     */
    updateFactType: flow(function* (factTypeId: string, request: UpdateFactTypeRequest) {
      try {
        const result = yield factTypesApi.updateFactType(factTypeId, request);
        if (result.success && result.data) {
          const factTypeModel = createFactTypeModelFromResponse(result.data);
          self.factTypes.set(factTypeId, factTypeModel);
          return { success: true, data: factTypeModel };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to update fact type')
        };
      }
    }),

    /**
     * Delete a fact type
     * Note: This action should only be available to users with appropriate permissions
     */
    deleteFactType: flow(function* (factTypeId: string) {
      try {
        const result = yield factTypesApi.deleteFactType(factTypeId);
        if (result.success) {
          self.factTypes.delete(factTypeId);
          return { success: true };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to delete fact type')
        };
      }
    })
  }));

export type FactTypesStoreType = Instance<typeof FactTypesStore>;

// Export singleton instance
export const factTypesStore = FactTypesStore.create({
  isLoading: false
});
